import { Screens } from "helpers/screens";
import { ReactElement } from "react";
import styled from "styled-components";
import { useMediaContext } from "lib/media-query";
import { Card } from "./elements";

type Props = {
    tips: {
        _id: string;
        title: string;
        slug: string;
        attachment: { fields: { file: { url: string } } };
        employeeBadge: { fields: { file: { url: string } } };
        dynamicPage?: {
            path: string;
            [key: string]: unknown;
        };
    }[];
};

const ContainerWrapper = styled.div`
    @media (${Screens.get("md")}) {
        height: 37.5625rem;
    }
`;

const CardContainer = styled.div`
    display: grid;
    gap: 2rem;
    position: relative;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    grid-template-columns: 1fr 1fr 1fr;
    @media (${Screens.get("md")}) {
        bottom: 12.5rem;
        padding: 0rem 3.5rem;
        overflow-x: unset;
    }
    ::-webkit-scrollbar {
        display: none;
        width: 0;
    }
`;

const Main = styled.div`
    background-color: #e3f6f8;
    border-radius: 3rem 0rem;
    padding: 3rem 1.25rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    @media (${Screens.get("md")}) {
        height: 26.0625rem;
        padding: 3rem 1.5rem;
        justify-content: space-between;
        flex-direction: row;
        padding: 3.5rem 3.53125rem;
    }
`;

const MainContentWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;
    height: fit-content;
`;

const HeaderContainer = styled.div`
    display: flex;
    gap: 1rem;
    flex-direction: column;
    @media (${Screens.get("md")}) {
        gap: 1.5rem;
        width: 29.75rem;
    }
`;

const Header = styled.header`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-family: ${({ theme }) => theme.typography.fontFamily.secondary};
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 2.125rem;
    @media (${Screens.get("md")}) {
        font-size: 2.5rem;
        font-weight: 600;
        line-height: 2.625rem;
    }
`;

const SubHeader = styled.span`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-family: ${({ theme }) => theme.typography.fontFamily.primary};
    font-size: 1.125rem;
    font-weight: 400;
    line-height: 1.75rem;
`;

const StyledButton = styled.a`
    align-items: center;
    background-color: transparent;
    border-radius: 0.75rem;
    border: ${({ theme }) => `0.125rem solid ${theme.colors.secondary[700]}`};
    color: ${({ theme }) => theme.colors.secondary[700]};
    display: flex;
    font-weight: 600;
    height: 3.5rem;
    justify-content: center;
    outline: none;
    padding: 0.96875rem 1.875rem;

    @media (${Screens.get("lg")}) {
        height: 3.5rem;
        margin-top: 0;
        width: 11.6875rem;
    }

    :focus {
        outline: none;
    }

    :hover {
        background: ${({ theme }) => theme.colors.secondary[400]};
        color: ${({ theme }) => theme.colors.white};
        text-decoration: none;
    }

    :active {
        background: ${({ theme }) => theme.colors.secondary[800]};
    }
`;

const mobileLayout = ({ tips }: Props): ReactElement => (
    <ContainerWrapper>
        <Main>
            <HeaderContainer>
                <Header>Tips van onze experts</Header>
                <SubHeader>
                    Ons team staat voor je klaar met de laatste tips & tricks in de wereld van
                    auto’s en occasions.
                </SubHeader>
            </HeaderContainer>
            <CardContainer>
                {tips.map((tip: Record<string, any>) => {
                    return (
                        <Card
                            key={tip._id}
                            header={tip.title}
                            image={tip.attachment.fields.file.url}
                            slug={tip.slug}
                            employeeBadge={tip.employeeBadge.fields.file.url}
                            dynamicUrlContentLink={tip?.dynamicPage?.path}
                        />
                    );
                })}
            </CardContainer>
            <StyledButton href="/blog">Bekijk alle tips</StyledButton>
        </Main>
    </ContainerWrapper>
);

export const Tips = ({ tips }: Props): ReactElement | null => {
    const mediaContext = useMediaContext();

    if (!tips.length) return null;

    const isMobile = mediaContext.isMobile || mediaContext.isMobilePlus;

    if (isMobile) return mobileLayout({ tips });

    return (
        <ContainerWrapper>
            <Main>
                <MainContentWrapper>
                    <HeaderContainer>
                        <Header>Tips van onze experts</Header>
                        <SubHeader>
                            Ons team staat voor je klaar met de laatste tips & tricks in de wereld
                            van auto’s en occasions.
                        </SubHeader>
                    </HeaderContainer>
                    <StyledButton href="/blog">Bekijk alle tips</StyledButton>
                </MainContentWrapper>
            </Main>
            <CardContainer>
                {tips.map((tip) => (
                    <Card
                        key={tip._id}
                        header={tip.title}
                        image={tip.attachment.fields.file.url}
                        slug={tip.slug}
                        employeeBadge={tip.employeeBadge.fields.file.url}
                        dynamicUrlContentLink={tip?.dynamicPage?.path}
                    />
                ))}
            </CardContainer>
        </ContainerWrapper>
    );
};
